(() => {
  const initialiseProductGallery = (root = document) => {
    const page = root.querySelector?.('.pu-product-page');
    if (!page || page.dataset.productGalleryReady === 'true') return;

    const gallery = page.querySelector('.app-product-container-gallery');
    const track = gallery?.querySelector('[data-product-gallery-track]');
    const slides = [...(track?.querySelectorAll('[data-product-gallery-slide]') || [])];
    if (!gallery || !track || !slides.length) return;

    page.dataset.productGalleryReady = 'true';
    const mobile = window.matchMedia('(max-width: 749px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const enhancedCarousel = page.classList.contains('pu-product-page--red-light-v2');
    // Project Unity product media is always customer-controlled. Keep arrows,
    // thumbnails, keyboard, drag and swipe, but never advance automatically.
    const autoplayEnabled = false;
    const thumbs = [...gallery.querySelectorAll('[data-product-gallery-thumb]')];
    const thumbnailRail = gallery.querySelector('.app-product-container-gallery-secondary');
    const showAllMedia = gallery.querySelector('[data-product-gallery-more]');
    const previous = gallery.querySelector('.additional-photo-left');
    const next = gallery.querySelector('.additional-photo-right');
    const current = gallery.querySelector('.additional-photo-current');
    const total = gallery.querySelector('.additional-photo-total');
    let activeIndex = 0;
    let scrollFrame = 0;
    let autoplayTimer = 0;
    let interactionTimer = 0;
    let isHovered = false;
    let hasFocus = false;
    let isDragging = false;
    let spaceViewerOpen = false;
    let dragStartX = 0;
    let dragStartScroll = 0;

    if (enhancedCarousel) {
      gallery.setAttribute('role', 'region');
      gallery.setAttribute('aria-roledescription', 'carousel');
      gallery.setAttribute('aria-label', 'Product media');
      track.tabIndex = 0;
      track.setAttribute('aria-label', 'Product media. Use the arrow keys, swipe or drag to browse.');
      slides.forEach((slide, index) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', `${index + 1} of ${slides.length}`);
        if (slide instanceof HTMLImageElement) slide.draggable = false;
      });
    }

    const sync = (index) => {
      activeIndex = (index + slides.length) % slides.length;
      if (current) current.textContent = String(activeIndex + 1);
      if (total) total.textContent = String(slides.length);
      thumbs.forEach((thumb, thumbIndex) => {
        const selected = thumbIndex === activeIndex;
        const state = selected ? 'true' : 'false';
        if (thumb.getAttribute('aria-current') !== state) thumb.setAttribute('aria-current', state);
        thumb.querySelector('img')?.classList.toggle('active', selected);
      });
      if (activeIndex > 2 && thumbnailRail) {
        thumbnailRail.classList.add('is-expanded');
        showAllMedia?.setAttribute('aria-expanded', 'true');
        const activeThumb = thumbs[activeIndex];
        if (activeThumb && thumbnailRail.scrollWidth > thumbnailRail.clientWidth) {
          const centredLeft = activeThumb.offsetLeft - ((thumbnailRail.clientWidth - activeThumb.offsetWidth) / 2);
          thumbnailRail.scrollTo({ left: Math.max(0, centredLeft), behavior: 'smooth' });
        }
      }
    };

    const showDesktop = (index) => {
      sync(index);
      slides.forEach((slide, slideIndex) => {
        slide.style.display = slideIndex === activeIndex ? 'block' : 'none';
      });
    };

    const showMobile = (index, behavior = 'smooth') => {
      // Read geometry before changing thumbnail state to avoid forcing layout
      // between a DOM write and the scroll operation.
      const nextIndex = (index + slides.length) % slides.length;
      const left = slides[nextIndex].offsetLeft;
      sync(index);
      track.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : behavior });
    };

    const showEnhanced = (index, behavior = 'smooth') => {
      if (mobile.matches) {
        showMobile(index, behavior);
        return;
      }
      // Desktop uses one stable active slide. Relying on a long horizontal
      // scroll offset left later thumbnails (including the approved stand
      // photograph) in a blank viewport in some browsers even though the
      // image had loaded. Inline-important display wins the legacy carousel
      // rule deterministically without changing the mobile swipe rail.
      sync(index);
      track.scrollLeft = 0;
      slides.forEach((slide, slideIndex) => {
        slide.style.setProperty('display', slideIndex === activeIndex ? 'block' : 'none', 'important');
      });
    };

    const pauseGalleryMedia = () => {
      gallery.querySelectorAll('.js-youtube').forEach((video) => {
        video.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });
      gallery.querySelectorAll('.js-vimeo').forEach((video) => {
        video.contentWindow?.postMessage('{"method":"pause"}', '*');
      });
      gallery.querySelectorAll('video').forEach((video) => video.pause());
      gallery.querySelectorAll('product-model').forEach((model) => model.modelViewerUI?.pause?.());
    };

    const show = (index, behavior) => {
      // Gallery changes must never stop the separate inline product film.
      // Pause only media owned by this gallery rather than every video on the page.
      if (index !== activeIndex) pauseGalleryMedia();
      if (enhancedCarousel) showEnhanced(index, behavior);
      else if (mobile.matches) showMobile(index, behavior);
      else showDesktop(index);
    };

    const clearAutoplay = () => {
      if (!autoplayTimer) return;
      window.clearTimeout(autoplayTimer);
      autoplayTimer = 0;
    };

    const scheduleAutoplay = () => {
      clearAutoplay();
      if (!autoplayEnabled || reducedMotion.matches || slides.length < 2 || isHovered || hasFocus || isDragging || spaceViewerOpen || document.hidden) return;
      autoplayTimer = window.setTimeout(() => {
        show(activeIndex + 1, 'smooth');
        scheduleAutoplay();
      }, 4200);
    };

    const pauseTemporarily = () => {
      if (!autoplayEnabled || reducedMotion.matches) return;
      clearAutoplay();
      if (interactionTimer) window.clearTimeout(interactionTimer);
      interactionTimer = window.setTimeout(scheduleAutoplay, 2200);
    };

    const handleArrow = (direction) => (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      show(activeIndex + direction);
      pauseTemporarily();
    };

    previous?.addEventListener('click', handleArrow(-1), true);
    next?.addEventListener('click', handleArrow(1), true);
    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => {
      show(index);
      pauseTemporarily();
    }));
    page.addEventListener('pu:product-media-show', (event) => {
      const index = Number(event.detail?.index);
      if (!Number.isInteger(index) || index < 0 || index >= slides.length) return;
      show(index, 'auto');
    });
    showAllMedia?.addEventListener('click', () => {
      thumbnailRail?.classList.add('is-expanded');
      showAllMedia.setAttribute('aria-expanded', 'true');
      thumbs[activeIndex]?.focus();
    });
    page.addEventListener('pu:space-viewer-open', () => {
      spaceViewerOpen = true;
      clearAutoplay();
    });
    page.addEventListener('pu:space-viewer-close', () => {
      spaceViewerOpen = false;
      scheduleAutoplay();
    });

    track.addEventListener('scroll', () => {
      if (!mobile.matches || scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = 0;
        const index = slides.reduce((nearest, slide, slideIndex) => (
          Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[nearest].offsetLeft - track.scrollLeft)
            ? slideIndex
            : nearest
        ), 0);
        // A swipe emits many scroll events within the same slide. Only update
        // the thumbnail DOM when the nearest slide actually changes.
        if (index !== activeIndex) sync(index);
      });
    }, { passive: true });

    if (enhancedCarousel) {
      gallery.addEventListener('mouseenter', () => {
        isHovered = true;
        clearAutoplay();
      });
      gallery.addEventListener('mouseleave', () => {
        isHovered = false;
        scheduleAutoplay();
      });
      gallery.addEventListener('focusin', () => {
        hasFocus = true;
        clearAutoplay();
      });
      gallery.addEventListener('focusout', (event) => {
        if (gallery.contains(event.relatedTarget)) return;
        hasFocus = false;
        scheduleAutoplay();
      });
      gallery.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        show(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
        pauseTemporarily();
      });
      track.addEventListener('wheel', pauseTemporarily, { passive: true });
      track.addEventListener('touchstart', () => {
        clearAutoplay();
      }, { passive: true });
      track.addEventListener('touchend', pauseTemporarily, { passive: true });

      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (event.target.closest('[data-pu-space-viewer-trigger]')) return;
        isDragging = true;
        dragStartX = event.clientX;
        dragStartScroll = track.scrollLeft;
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);
        clearAutoplay();
      });
      track.addEventListener('pointermove', (event) => {
        if (!isDragging) return;
        event.preventDefault();
        track.scrollLeft = dragStartScroll - (event.clientX - dragStartX);
      });
      const finishDrag = (event) => {
        if (!isDragging) return;
        isDragging = false;
        track.classList.remove('is-dragging');
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        const index = slides.reduce((nearest, slide, slideIndex) => (
          Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[nearest].offsetLeft - track.scrollLeft)
            ? slideIndex
            : nearest
        ), 0);
        show(index);
        pauseTemporarily();
      };
      track.addEventListener('pointerup', finishDrag);
      track.addEventListener('pointercancel', finishDrag);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) clearAutoplay();
        else scheduleAutoplay();
      });
      reducedMotion.addEventListener('change', scheduleAutoplay);
    }

    mobile.addEventListener('change', () => {
      slides.forEach((slide) => slide.style.removeProperty('display'));
      window.requestAnimationFrame(() => show(activeIndex, 'auto'));
    });
    window.requestAnimationFrame(() => {
      const requestedIndex = Number(page.dataset.variantMediaIndex);
      const initialIndex = Number.isInteger(requestedIndex) && requestedIndex >= 0 && requestedIndex < slides.length
        ? requestedIndex
        : 0;
      show(initialIndex, 'auto');
      scheduleAutoplay();
    });

    const ensureDesktopGalleryVisible = () => {
      if (mobile.matches || enhancedCarousel) return;
      if (slides.some((slide) => window.getComputedStyle(slide).display !== 'none')) return;
      showDesktop(activeIndex);
    };
    const visibilityObserver = new MutationObserver(ensureDesktopGalleryVisible);
    slides.forEach((slide) => visibilityObserver.observe(slide, { attributes: true, attributeFilter: ['style', 'class'] }));
    window.addEventListener('load', ensureDesktopGalleryVisible, { once: true });
    window.requestAnimationFrame(ensureDesktopGalleryVisible);
  };

  const initialiseBusinessDialog = (root = document) => {
    const page = root.querySelector?.('.pu-product-page');
    if (!page || page.dataset.businessDialogReady === 'true') return;

    const dialog = page.querySelector('[data-business-dialog]');
    const openButton = page.querySelector('[data-business-dialog-open]');
    const closeButton = page.querySelector('[data-business-dialog-close]');
    if (!dialog || !openButton || !closeButton) return;

    page.dataset.businessDialogReady = 'true';
    let returnFocus = openButton;

    const openDialog = () => {
      returnFocus = document.activeElement || openButton;
      dialog.showModal();
      closeButton.focus();
    };

    const closeDialog = () => {
      dialog.close();
      returnFocus?.focus?.();
    };

    openButton.addEventListener('click', openDialog);
    closeButton.addEventListener('click', closeDialog);
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });

  };

  const initialiseAutoplayVideos = (root = document) => {
    const videos = [...(root.querySelectorAll?.('[data-product-autoplay-video]') || [])];
    videos.forEach((container) => {
      if (container.dataset.autoplayVideoReady === 'true') return;
      const player = container.querySelector('[data-product-autoplay-video-player]');
      const toggle = container.querySelector('[data-product-autoplay-video-toggle]');
      if (!player || !toggle) return;

      container.dataset.autoplayVideoReady = 'true';
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let explicitlyPaused = false;

      // The poster and footage are attached lazily so they never compete with the
      // gallery cover during the initial load. Markup that still carries a poster
      // or source src (older sections) is attached immediately.
      let posterAttached = Boolean(player.poster);
      let sourcesAttached = ![...player.querySelectorAll('source[data-src]')].some((source) => !source.getAttribute('src'));
      const attachPoster = () => {
        if (posterAttached) return;
        posterAttached = true;
        if (player.dataset.poster) player.poster = player.dataset.poster;
      };
      const attachSources = () => {
        attachPoster();
        if (sourcesAttached) return;
        sourcesAttached = true;
        player.querySelectorAll('source[data-src]').forEach((source) => {
          if (!source.getAttribute('src')) source.setAttribute('src', source.dataset.src);
        });
        player.load();
      };

      const sync = () => {
        const paused = player.paused;
        container.dataset.videoState = paused ? 'paused' : 'playing';
        toggle.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
      };
      const pause = () => {
        player.pause();
        sync();
      };
      const play = () => {
        attachSources();
        player.muted = true;
        player.defaultMuted = true;
        player.play().then(sync).catch(() => {
          pause();
        });
      };

      toggle.addEventListener('click', () => {
        if (player.paused) {
          explicitlyPaused = false;
          play();
        } else {
          explicitlyPaused = true;
          pause();
        }
      });
      player.addEventListener('play', sync);
      player.addEventListener('pause', sync);
      reducedMotion.addEventListener('change', () => {
        if (reducedMotion.matches) pause();
        else if (!explicitlyPaused) play();
      });

      const start = () => {
        if (reducedMotion.matches) {
          player.removeAttribute('autoplay');
          attachPoster();
          pause();
        } else {
          play();
        }
      };
      if (posterAttached && sourcesAttached) start();
      else if (typeof window.IntersectionObserver === 'function') {
        const approach = new window.IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          approach.disconnect();
          start();
        }, { rootMargin: '600px 0px' });
        approach.observe(container);
      } else start();
    });
  };

  const initialisePurchaseOptions = (root = document) => {
    const page = root.querySelector?.('.pu-product-page');
    if (!page || page.dataset.purchaseOptionsReady === 'true') return;

    const purchaseOptions = [...page.querySelectorAll('.custom-product-list__toggle-item.app-product-page-item')];
    const purchaseGroup = page.querySelector('custom-product-list-toggle');
    const addToBasketLabel = page.querySelector('.pu-product-actions .app-product-page-item-title');
    const routeNote = page.querySelector('.pu-product-v2-route-note');
    if (!purchaseGroup || !purchaseOptions.length) return;

    page.dataset.purchaseOptionsReady = 'true';
    purchaseGroup.setAttribute('role', 'radiogroup');
    purchaseGroup.setAttribute('aria-label', 'Purchase option');

    const syncPurchaseOptions = () => {
      const selectedOption = purchaseOptions.find((option) => option.classList.contains('active'));
      purchaseOptions.forEach((option) => {
        const selected = option.classList.contains('active');
        option.setAttribute('role', 'radio');
        option.setAttribute('aria-checked', selected ? 'true' : 'false');
        // A required radiogroup starts with no selected route. Keep its first
        // option in the tab order so keyboard users can enter the group, then
        // switch to the selected option once a choice has been made.
        option.tabIndex = selected || (!selectedOption && option === purchaseOptions[0]) ? 0 : -1;
      });
      if (routeNote && addToBasketLabel && selectedOption) {
        addToBasketLabel.textContent = 'Checkout';
      }
      if (routeNote && selectedOption) {
        routeNote.textContent = selectedOption.dataset.rent === 'rent'
          ? 'Monthly rental selected. Rent to buy is available.'
          : 'One-time purchase selected.';
      }
    };

    purchaseOptions.forEach((option, index) => {
      option.addEventListener('click', () => window.requestAnimationFrame(syncPurchaseOptions));
      option.addEventListener('keydown', (event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          option.click();
          return;
        }
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const next = purchaseOptions[(index + direction + purchaseOptions.length) % purchaseOptions.length];
        next.click();
        next.focus();
      });
    });
    syncPurchaseOptions();
  };

  const initialiseProductStoryCarousels = (root = document) => {
    const carousels = [...(root.querySelectorAll?.('[data-product-story-carousel]') || [])];

    carousels.forEach((carousel) => {
      if (carousel.dataset.productStoryReady === 'true') return;

      const track = carousel.querySelector('[data-product-story-track]');
      const cards = [...(track?.querySelectorAll('[data-product-story-card]') || [])];
      const previous = carousel.querySelector('[data-product-story-previous]');
      const next = carousel.querySelector('[data-product-story-next]');
      const current = carousel.querySelector('[data-product-story-current]');
      const total = carousel.querySelector('[data-product-story-total]');
      if (!track || !cards.length || !previous || !next) return;

      carousel.dataset.productStoryReady = 'true';
      let activeIndex = 0;
      let scrollFrame = 0;

      const sync = (index) => {
        activeIndex = (index + cards.length) % cards.length;
        if (current) current.textContent = String(activeIndex + 1);
        if (total) total.textContent = String(cards.length);
      };

      const show = (index, behavior = 'smooth') => {
        sync(index);
        track.scrollTo({ left: cards[activeIndex].offsetLeft, behavior });
      };

      previous.addEventListener('click', () => show(activeIndex - 1));
      next.addEventListener('click', () => show(activeIndex + 1));
      track.addEventListener('scroll', () => {
        if (scrollFrame) return;
        scrollFrame = window.requestAnimationFrame(() => {
          scrollFrame = 0;
          const index = cards.reduce((nearest, card, cardIndex) => (
            Math.abs(card.offsetLeft - track.scrollLeft) < Math.abs(cards[nearest].offsetLeft - track.scrollLeft)
              ? cardIndex
              : nearest
          ), 0);
          sync(index);
        });
      }, { passive: true });
      window.addEventListener('resize', () => window.requestAnimationFrame(() => show(activeIndex, 'auto')));
      sync(0);
    });
  };

  const initialiseProductReviews = (root = document) => {
    const sections = [...(root.querySelectorAll?.('[data-pu-product-reviews]') || [])];

    sections.forEach((section) => {
      if (section.dataset.puProductReviewsReady === 'true') return;

      const rail = section.querySelector('[data-pu-product-reviews-rail]');
      const reviews = [...(rail?.querySelectorAll('[data-pu-product-review]') || [])];
      const filters = [...section.querySelectorAll('[data-pu-product-reviews-filter]')];
      const previous = section.querySelector('[data-pu-product-reviews-previous]');
      const next = section.querySelector('[data-pu-product-reviews-next]');
      if (!rail || !reviews.length || !previous || !next) return;

      section.dataset.puProductReviewsReady = 'true';
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let scrollFrame = 0;

      const visibleReviews = () => reviews.filter((review) => !review.classList.contains('is-hidden'));

      const stopEmbeds = () => {
        reviews.forEach((review) => {
          review.querySelector('.pu-product-review__embed')?.remove();
          review.classList.remove('is-playing');
        });
      };

      const updateControls = () => {
        const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
        previous.disabled = rail.scrollLeft <= 2;
        next.disabled = rail.scrollLeft >= maxScroll - 2;
      };

      const activeIndex = () => {
        const visible = visibleReviews();
        if (!visible.length) return 0;
        return visible.reduce((nearest, review, index) => (
          Math.abs(review.offsetLeft - rail.scrollLeft) < Math.abs(visible[nearest].offsetLeft - rail.scrollLeft)
            ? index
            : nearest
        ), 0);
      };

      const show = (index) => {
        const visible = visibleReviews();
        if (!visible.length) return;
        const target = visible[Math.max(0, Math.min(visible.length - 1, index))];
        rail.scrollTo({ left: target.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      };

      filters.forEach((filter) => {
        filter.addEventListener('click', () => {
          stopEmbeds();
          const selected = filter.dataset.puProductReviewsFilter;
          filters.forEach((other) => {
            const active = other === filter;
            other.classList.toggle('is-active', active);
            other.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          reviews.forEach((review) => {
            review.classList.toggle('is-hidden', selected !== 'all' && !(review.dataset.storyType || '').split(' ').includes(selected));
          });
          rail.scrollTo({ left: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
          window.requestAnimationFrame(updateControls);
        });
      });

      reviews.forEach((review) => {
        review.addEventListener('click', () => {
          if (!review.dataset.storyVideo || review.classList.contains('is-playing')) return;
          stopEmbeds();
          const media = review.querySelector('.pu-product-review__media');
          if (!media) return;
          let embed;
          if (/\.mp4(\?|$)/i.test(review.dataset.storyVideo)) {
            embed = document.createElement('video');
            embed.className = 'pu-product-review__embed';
            embed.src = review.dataset.storyVideo;
            if (review.dataset.storyPoster) embed.poster = review.dataset.storyPoster;
            embed.controls = true;
            embed.autoplay = true;
            embed.playsInline = true;
            embed.preload = 'metadata';
            embed.setAttribute('aria-label', `${review.dataset.storyPerson || 'Oxydise client'} story video`);
          } else {
            const separator = review.dataset.storyVideo.includes('?') ? '&' : '?';
            embed = document.createElement('iframe');
            embed.className = 'pu-product-review__embed';
            embed.src = `${review.dataset.storyVideo}${separator}autoplay=1&playsinline=1`;
            embed.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
            embed.setAttribute('allowfullscreen', '');
            embed.setAttribute('title', `${review.dataset.storyPerson || 'Oxydise client'} story video`);
          }
          media.appendChild(embed);
          if (embed.play) embed.play().catch(() => {});
          review.classList.add('is-playing');
        });
      });

      previous.addEventListener('click', () => show(activeIndex() - 1));
      next.addEventListener('click', () => show(activeIndex() + 1));
      rail.addEventListener('scroll', () => {
        if (scrollFrame) return;
        scrollFrame = window.requestAnimationFrame(() => {
          scrollFrame = 0;
          updateControls();
        });
      }, { passive: true });
      window.addEventListener('resize', () => window.requestAnimationFrame(updateControls));
      window.requestAnimationFrame(updateControls);
    });
  };

  const initialiseProductGuides = (root = document) => {
    const guides = [...(root.querySelectorAll?.('[data-pu-product-guide]') || [])];

    guides.forEach((guide) => {
      if (guide.dataset.puProductGuideReady === 'true') return;

      const tabs = [...guide.querySelectorAll('[data-pu-guide-tab]')];
      const panels = [...guide.querySelectorAll('[data-pu-guide-panel]')];
      if (!tabs.length || !panels.length) return;
      guide.dataset.puProductGuideReady = 'true';
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const guideLabel = guide.dataset.puProductGuideLabel || 'Product details';
      const itemLabel = guide.dataset.puProductGuideItemLabel || 'item';

      const activeIndexFor = (panel) => {
        const track = panel.querySelector('[data-pu-guide-track]');
        const cards = [...(track?.querySelectorAll('[data-pu-guide-card]') || [])];
        if (!track || !cards.length) return 0;
        const padding = Number.parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
        return cards.reduce((nearest, card, index) => (
          Math.abs(card.offsetLeft - padding - track.scrollLeft)
            < Math.abs(cards[nearest].offsetLeft - padding - track.scrollLeft)
            ? index
            : nearest
        ), 0);
      };

      const updateNav = (panel) => {
        const track = panel.querySelector('[data-pu-guide-track]');
        const cards = [...(track?.querySelectorAll('[data-pu-guide-card]') || [])];
        const previous = panel.querySelector('[data-pu-guide-previous]');
        const next = panel.querySelector('[data-pu-guide-next]');
        const announcement = panel.querySelector('[data-pu-guide-announcement]');
        if (!track || !cards.length || !previous || !next) return;
        const activeIndex = activeIndexFor(panel);
        const end = Math.max(0, track.scrollWidth - track.clientWidth);
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= end - 2;
        if (announcement) announcement.textContent = `Showing ${itemLabel} ${activeIndex + 1} of ${cards.length}.`;
      };

      const setTab = (name, moveFocus = false) => {
        tabs.forEach((tab) => {
          const selected = tab.dataset.puGuideTab === name;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.tabIndex = selected ? 0 : -1;
          if (selected) {
            if (moveFocus) tab.focus();
            const tabRow = tab.parentElement;
            if (tabRow && tabRow.scrollWidth > tabRow.clientWidth + 2) {
              const centredLeft = tab.offsetLeft - ((tabRow.clientWidth - tab.offsetWidth) / 2);
              const maxLeft = Math.max(0, tabRow.scrollWidth - tabRow.clientWidth);
              tabRow.scrollTo({
                left: Math.max(0, Math.min(centredLeft, maxLeft)),
                behavior: reducedMotion.matches ? 'auto' : 'smooth'
              });
            }
          }
        });
        panels.forEach((panel) => {
          const selected = panel.dataset.puGuidePanel === name;
          panel.hidden = !selected;
          if (selected) {
            const track = panel.querySelector('[data-pu-guide-track]');
            if (track) track.scrollLeft = 0;
            window.requestAnimationFrame(() => updateNav(panel));
          }
        });
      };

      panels.forEach((panel) => {
        const track = panel.querySelector('[data-pu-guide-track]');
        const cards = [...(track?.querySelectorAll('[data-pu-guide-card]') || [])];
        const previous = panel.querySelector('[data-pu-guide-previous]');
        const next = panel.querySelector('[data-pu-guide-next]');
        if (!track || !cards.length || !previous || !next) return;

        track.tabIndex = 0;
        track.setAttribute('aria-label', `${guideLabel}. Use the arrow keys or controls to move between cards.`);

        let scrollFrame = 0;
        const trackPadding = () => Number.parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
        const show = (index) => {
          const target = cards[Math.max(0, Math.min(cards.length - 1, index))];
          track.scrollTo({ left: target.offsetLeft - trackPadding(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        };

        previous.addEventListener('click', () => show(activeIndexFor(panel) - 1));
        next.addEventListener('click', () => show(activeIndexFor(panel) + 1));
        track.addEventListener('scroll', () => {
          if (scrollFrame) return;
          scrollFrame = window.requestAnimationFrame(() => {
            scrollFrame = 0;
            updateNav(panel);
          });
        }, { passive: true });
        track.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = activeIndexFor(panel);
          if (event.key === 'ArrowLeft') nextIndex -= 1;
          if (event.key === 'ArrowRight') nextIndex += 1;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = cards.length - 1;
          show(nextIndex);
        });

        let dragPointer = null;
        let dragStartX = 0;
        let dragStartScroll = 0;
        const endDrag = (event) => {
          if (dragPointer === null || event.pointerId !== dragPointer) return;
          if (track.hasPointerCapture?.(dragPointer)) track.releasePointerCapture(dragPointer);
          dragPointer = null;
          track.classList.remove('is-dragging');
          show(activeIndexFor(panel));
        };
        track.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch' || event.button !== 0) return;
          dragPointer = event.pointerId;
          dragStartX = event.clientX;
          dragStartScroll = track.scrollLeft;
          track.setPointerCapture?.(dragPointer);
          track.classList.add('is-dragging');
        });
        track.addEventListener('pointermove', (event) => {
          if (dragPointer === null || event.pointerId !== dragPointer) return;
          track.scrollLeft = dragStartScroll - (event.clientX - dragStartX);
        });
        track.addEventListener('pointerup', endDrag);
        track.addEventListener('pointercancel', endDrag);
        updateNav(panel);
      });

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => setTab(tab.dataset.puGuideTab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          setTab(tabs[nextIndex].dataset.puGuideTab, true);
        });
      });

      window.addEventListener('resize', () => {
        const activePanel = panels.find((panel) => !panel.hidden);
        if (activePanel) window.requestAnimationFrame(() => updateNav(activePanel));
      });
    });
  };

  document.querySelectorAll('.pu-product-page').forEach((page) => {
    initialiseProductGallery(page.parentElement);
    initialiseAutoplayVideos(page.parentElement);
    initialiseBusinessDialog(page.parentElement);
    initialisePurchaseOptions(page.parentElement);
    initialiseProductStoryCarousels(page.parentElement);
    initialiseProductReviews(page.parentElement);
    initialiseProductGuides(page.parentElement);
  });

  // External video facade: the YouTube or Vimeo player is only fetched when
  // someone presses our play button, so the resting frame stays Oxydise's.
  function initialiseExternalVideoFacades(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-pu-external-video]:not([data-pu-external-ready])').forEach(function (player) {
      player.dataset.puExternalReady = 'true';
      var button = player.querySelector('[data-pu-external-play]');
      if (!button) return;
      button.addEventListener('click', function () {
        var src = player.dataset.embed;
        if (!src) return;
        // Hide our still and play control in the tap frame, then build the
        // player once that has painted: inserting the cross-origin iframe took
        // 80-90 ms on a throttled phone (INP). YouTube autoplay still works, as
        // it rests on the page's sticky user activation rather than this task.
        player.dataset.videoState = 'playing';
        button.remove();
        window.requestAnimationFrame(function () {
          window.setTimeout(function () {
            var frame = document.createElement('iframe');
            frame.setAttribute('src', src);
            frame.setAttribute('title', player.dataset.title || 'Video');
            frame.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
            frame.setAttribute('allowfullscreen', '');
            player.appendChild(frame);
          }, 0);
        });
      }, { once: true });
    });
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseProductGallery(event.target);
    initialiseAutoplayVideos(event.target);
    initialiseBusinessDialog(event.target);
    initialisePurchaseOptions(event.target);
    initialiseProductStoryCarousels(event.target);
    initialiseProductReviews(event.target);
    initialiseProductGuides(event.target);
    initialiseExternalVideoFacades(event.target);
  });
  initialiseExternalVideoFacades(document);
})();

(() => {
  const root = document.querySelector('.pu-product-page--rental-preview');
  const details = root?.querySelector('.pu-rental-preview-details');
  const trigger = details?.querySelector('summary');
  if (!trigger || typeof HTMLDialogElement === 'undefined') return;
  const dialog = document.createElement('dialog');
  dialog.className = 'pu-rental-help-drawer';
  dialog.setAttribute('aria-labelledby', 'pu-rental-help-title');
  const heading = document.createElement('h2');
  heading.id = 'pu-rental-help-title';
  heading.textContent = trigger.textContent;
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'pu-rental-help-drawer__close';
  close.setAttribute('aria-label', root.dataset.rentalClose || 'Close rental information');
  close.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6.5 6.5 11 11m0-11-11 11"/></svg>';
  const header = document.createElement('header');
  header.append(heading, close);
  dialog.append(header);
  [...details.children].filter(child => child !== trigger).forEach(child => dialog.append(child.cloneNode(true)));
  document.body.append(dialog);
  trigger.setAttribute('aria-haspopup', 'dialog');
  let opening = false;
  trigger.addEventListener('click', event => {
    event.preventDefault();
    details.open = false;
    // showModal() turns the whole page behind the drawer inert, which restyles
    // every element and kept this tap from painting for ~100 ms on a throttled
    // phone (INP). Let the tap paint first, then open; a second tap in that
    // gap is ignored rather than opening the drawer twice.
    if (opening || dialog.open) return;
    opening = true;
    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        opening = false;
        if (dialog.open) return;
        dialog.showModal();
        document.documentElement.classList.add('pu-rental-help-open');
        close.focus();
      }, 0);
    });
  });
  close.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const r = dialog.getBoundingClientRect();
    if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close();
  });
  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('pu-rental-help-open');
    trigger.focus();
  });
})();
